import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { FiArrowRight, FiCalendar, FiClock, FiSearch } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

const Blogs = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const blogs = [
    {
      id: 1,
      title: "The Future of AI in Enterprise: Transforming Business Operations",
      excerpt: "Discover how artificial intelligence is revolutionizing enterprise operations, from automated workflows to predictive analytics that drive strategic decision-making.",
      author: "Sarah Johnson",
      date: "December 15, 2024",
      readTime: "8 min read",
      category: "Artificial Intelligence",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop&crop=entropy&auto=format",
      gradient: "from-purple-500 to-pink-600",
      featured: true
    },
    {
      id: 2,
      title: "Cloud Migration Strategies: Best Practices for Modern Enterprises",
      excerpt: "Learn the essential strategies and methodologies for successful cloud migration, including security considerations and cost optimization techniques.",
      author: "Michael Chen",
      date: "December 12, 2024",
      readTime: "6 min read",
      category: "Cloud Infrastructure",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop&crop=entropy&auto=format",
      gradient: "from-blue-500 to-indigo-600"
    },
    {
      id: 3,
      title: "Cybersecurity in 2024: Emerging Threats and Defense Strategies",
      excerpt: "Stay ahead of evolving cyber threats with comprehensive security frameworks and proactive defense mechanisms tailored for modern businesses.",
      author: "David Rodriguez",
      date: "December 10, 2024",
      readTime: "10 min read",
      category: "Cybersecurity",
      image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&h=400&fit=crop&crop=entropy&auto=format",
      gradient: "from-red-500 to-orange-600"
    },
    {
      id: 4,
      title: "Building Agile Development Teams: A Complete Guide to Success",
      excerpt: "Explore proven methodologies for assembling high-performing agile teams that deliver exceptional results in fast-paced development environments.",
      author: "Emily Thompson",
      date: "December 8, 2024",
      readTime: "7 min read",
      category: "Project Management",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop&crop=entropy&auto=format",
      gradient: "from-green-500 to-teal-600"
    },
    {
      id: 5,
      title: "Digital Transformation: A Strategic Roadmap for Success",
      excerpt: "Navigate the complex landscape of digital transformation with our comprehensive guide covering strategy, implementation, and measurement of success.",
      author: "Alex Kumar",
      date: "December 5, 2024",
      readTime: "12 min read",
      category: "Digital Transformation",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop&crop=entropy&auto=format",
      gradient: "from-indigo-500 to-purple-600"
    },
    {
      id: 6,
      title: "Remote Work Technology: Tools and Best Practices",
      excerpt: "Optimize your remote work setup with the latest tools and technologies that enhance productivity and collaboration in distributed teams.",
      author: "Lisa Wong",
      date: "December 3, 2024",
      readTime: "5 min read",
      category: "Technology",
      image: "https://images.unsplash.com/photo-1587440871875-191322ee64b0?w=600&h=400&fit=crop&crop=entropy&auto=format",
      gradient: "from-teal-500 to-cyan-600"
    }
  ];

  const categories = ['All', ...new Set(blogs.map(blog => blog.category))];

  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.author.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || blog.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <span className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-4 font-accent tracking-wide">
              Technology Insights
            </span>
            <h1 className="text-5xl md:text-6xl font-bold leading-tight font-display tracking-tight text-gray-900 mb-6">
              Our
              <span className="block bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent pb-2">
                Technology Blogs
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Explore insights, trends, and best practices in technology, digital transformation, 
              and business innovation from our team of experts.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
          >
            <div className="flex flex-col md:flex-row gap-6">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="md:w-64">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results count */}
            <div className="mt-4 text-sm text-gray-600 font-body">
              {filteredBlogs.length} article{filteredBlogs.length !== 1 ? 's' : ''} found
              {selectedCategory !== 'All' && ` in ${selectedCategory}`}
              {searchTerm && ` for "${searchTerm}"`}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.map((blog, index) => (
              <motion.div
                key={blog.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group cursor-pointer"
              >
                <Link to={`/blog/${blog.id}`}>
                  <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 relative">
                    {blog.featured && (
                      <div className="absolute top-4 right-4 z-10">
                        <span className="px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-medium rounded-full font-accent tracking-wide">
                          Featured
                        </span>
                      </div>
                    )}

                    {/* Blog Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={blog.image}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span className={`px-3 py-1 bg-gradient-to-r ${blog.gradient} text-white text-xs font-medium rounded-full font-accent tracking-wide`}>
                          {blog.category}
                        </span>
                      </div>
                    </div>

                    {/* Blog Content */}
                    <div className="p-6">
                      <div className="flex items-center space-x-3 text-xs text-gray-500 mb-3 font-body">
                        <div className="flex items-center space-x-1">
                          <FiCalendar size={14} />
                          <span>{blog.date}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <FiClock size={14} />
                          <span>{blog.readTime}</span>
                        </div>
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors duration-300 font-heading tracking-wide leading-tight">
                        {blog.title}
                      </h3>
                      
                      <p className="text-gray-600 text-sm mb-4 font-body leading-relaxed line-clamp-3">
                        {blog.excerpt}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-medium font-body">
                              {blog.author.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <span className="text-sm text-gray-600 font-body">{blog.author}</span>
                        </div>
                        
                        <motion.div
                          whileHover={{ x: 5 }}
                          className="text-indigo-600 group-hover:text-indigo-700"
                        >
                          <FiArrowRight size={18} />
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* No results */}
          {filteredBlogs.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="text-gray-400 mb-4">
                <FiSearch size={64} className="mx-auto" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 font-heading">No articles found</h3>
              <p className="text-gray-600 font-body">
                Try adjusting your search terms or selecting a different category.
              </p>
            </motion.div>
          )}

          {/* Load More Button */}
          {filteredBlogs.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-center mt-16"
            >
            {/* Unhide this button when we have more blogs */}
              {/* <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-10 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 font-accent tracking-wide"
              >
                Load More Articles
              </motion.button> */}
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blogs; 